import React, { useEffect, useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { fetch_rule_history } from './api';
import {
  changed_fields_text,
  refusal,
  version_row,
  version_status_view,
  version_title
} from './logic';

/*
 * PROD-7828 R14 P4 — every version of one rule, newest effective date first (GET /rules/history).
 * highlight_version_id: the version a payment line used; its row is marked and named at the top.
 * props: rule_key, highlight_version_id?, on_close, on_error(e, fallback) for a permission failure.
 */
const COLUMNS = [
  {
    key: 'version_no',
    label: 'Version',
    hideInCard: true,
    render: (r) => <span className="tp-strong">v{r.version_no}</span>
  },
  { key: 'effective_text', label: 'From (IST)' },
  {
    key: 'status',
    label: 'Status',
    render: (r) => {
      const s = version_status_view(r.status);
      return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
    }
  },
  { key: 'sentence', label: 'What it does' },
  {
    key: 'teacher',
    label: 'Teachers read',
    render: (r) =>
      r.teacher_title ? (
        <span>
          <span className="tp-strong">{r.teacher_title}</span>
          {r.teacher_why ? <div className="tp-hint">{r.teacher_why}</div> : null}
        </span>
      ) : (
        <span className="tp-hint">Not shown to teachers</span>
      )
  },
  {
    key: 'changed',
    label: 'What changed',
    render: (r) => (r.is_seed ? 'First version' : changed_fields_text(r.changed_fields) || '—')
  },
  {
    key: 'note',
    label: 'Note',
    render: (r) => (
      <span style={{ whiteSpace: 'pre-wrap' }}>
        {r.is_seed ? 'Seeded from the old engine' : r.note}
      </span>
    )
  },
  {
    key: 'who',
    label: 'Changed by',
    render: (r) => (
      <span>
        <div>{r.changed_by}</div>
        {r.changed_at ? <div className="tp-hint">{r.changed_at}</div> : null}
      </span>
    )
  }
];

const RuleHistoryDialog = ({ rule_key, highlight_version_id, on_close, on_error }) => {
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: '' }));
    fetch_rule_history(rule_key)
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data, error: '' });
      })
      .catch((e) => {
        if (cancelled) return;
        const r = refusal(e, 'Could not load the rule history.');
        if (r.permission && on_error) on_error(e, r.text);
        setState({ status: 'error', data: null, error: r.text });
      });
    return () => {
      cancelled = true;
    };
  }, [rule_key, rev, on_error]);

  const versions = (state.data && Array.isArray(state.data.versions) && state.data.versions) || [];
  const rows = versions.map(version_row);
  const used = highlight_version_id
    ? versions.find((v) => v.version_id === highlight_version_id)
    : null;
  const name = rows.length ? (rows.find((r) => r.status === 'CURRENT') || rows[0]).name : '';

  return (
    <TpDialog title={name ? `Rule history: ${name}` : 'Rule history'} on_close={on_close}>
      <div className="tp-modal tp-modal-wide">
        {state.status === 'loading' ? (
          <div className="tp-empty" role="status">
            Loading rule history…
          </div>
        ) : null}
        {state.status === 'error' ? (
          <LoadFailed text={state.error} on_retry={() => setRev((r) => r + 1)} />
        ) : null}
        {state.status === 'ready' && used ? (
          <div className="tp-banner tp-banner-info" role="note">
            This line was worked out by {version_title(used)}.
          </div>
        ) : null}
        {state.status === 'ready' && highlight_version_id && !used ? (
          <div className="tp-banner tp-banner-warn" role="note">
            The version this line names is not in this rule&apos;s history.
          </div>
        ) : null}
        {state.status === 'ready' ? (
          <p className="tp-hint" style={{ margin: '0 0 12px' }}>
            Each class uses the version in force at its scheduled start (IST); month amounts use the
            version in force at the month&apos;s end. Versions are never edited or removed.
          </p>
        ) : null}
        {state.status === 'ready' ? (
          <DataTable
            columns={COLUMNS}
            rows={rows}
            rowKey={(r) => r.key}
            cardTitle={(r) => <span className="tp-strong">v{r.version_no}</span>}
            rowClassName={(r) => (r.version_id === highlight_version_id ? 'tp-row-hl' : '')}
            emptyText="This rule has no versions."
          />
        ) : null}
      </div>
    </TpDialog>
  );
};

export default RuleHistoryDialog;
